import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  PrimaryButton,
  DefaultButton,
  Icon,
  TextField,
  ComboBox,
  ChoiceGroup,
} from "@fluentui/react";
import { t } from "i18next";
import "./AINodesConfig.css";
import themeContext from "../../../context/theme/ThemeContext";
import loaderContext from "../../../context/loader/LoaderContext";
import toastContext from "../../../context/toast/ToastContext";
import userContext from "../../../context/users/UserContext";
import CustomBreadCrumb from "../../../components/CustomBreadCrumb";
import ConfirmModal from "../../../components/Modal/ConfirmModal";
import MultiSelectClientDropdown from "../../MyTemplate/Components/MultiSelectClientDropdown";
import CustomToolTipHost from "../../../components/CustomToolTipHost/CustomToolTipHost";
import { deleteAINodeDetails, getAINodeDetails, saveAINode } from "../../../service/MasterService";
import { TOAST_TYPE } from "../../../constants/Constants";
import { echoPacsNode } from "../../../service/FirmServices";
import OutsideClickHandler from "react-outside-click-handler";
import SearchableDropDown from "../../ReportTemplates/helper/SearchDropdown";
import { ClearIcon } from "@fluentui/react-icons-mdl2";

const AINodesConfig = () => {
  const { getThemeColor, getAlterColor } = useContext(themeContext);
  const { setLoaderRunning, ref } = useContext(loaderContext);
  const { showToastv2 } = useContext(toastContext);
  const { loadByUsername, authenticated } = useContext(userContext);

  const accentBlue = getThemeColor("active-side-tab");

  const MAX = {
    name: 50,
    aeTitle: 16,
    responseAETitle: 16,
    hostname: 255,
    port: 5, // digits
    delay: 5, // digits
    studyDescription: 255,
  };

  const descriptionTypeOptions = [
    { key: 0, text: "Partial"},
    { key: 1, text: "Exact" },
  ];

  const ruleTypeOptions = [
    { key: 0, text: "Include"},
    { key: 1, text: "Exclude" },
  ];

  const choiceGroupStyles = {
    label: {
      display: "inline",
    },
    flexContainer: {
      columnGap: "1em",
      display: "inline-flex",
      flexDirection: "row",
      flexWrap: "wrap",
    },
  };

  const requiredStyle = { color: 'red' };

  const [nodes, setNodes] = useState([]);
  const [modalitiesSubModalities, setModalitiesSubModalities] = useState([]);
  const [modalityOptions, setModalityOptions] = useState([]);
  const [filteredModalityOptions, setFilteredModalityOptions] = useState([]);
  const [filteredSubModalityOptions, setFilteredSubModalityOptions] = useState({});
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [backupNode, setBackupNode] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  useEffect(() => {
    if (!authenticated) loadByUsername();
    init();
  }, [authenticated]);

  const init = async () => {
    setLoaderRunning(true); 
    ref.current.continuousStart();
    try {
        const response = await getAINodeDetails();
        console.log("AINodeDetails response:", response);

        setClientOptions((response.data.data.clientsList || []).map((c) => ({
            value: c.id,
            label: c.name,
        })));
        setModalitiesSubModalities(response.data.data.modalityWithSubModality || []);
        const options = (response.data.data.modalityWithSubModality || []).map((m) => ({
            key: m.id,
            text: m.name,
        }));
        setModalityOptions(options);
        setFilteredModalityOptions(options);

        setNodes(response.data.data.aiNodeList || []);
        
    } catch (err) {
        console.error(err);
        showToastv2(TOAST_TYPE.ERROR, "Initial load failed");
    } finally {
        setLoaderRunning(false); ref.current.complete();
    }
  };

  const getSubmodalityOptions = (modalityId) => {
    const modality = modalitiesSubModalities.find((m) => m.id === modalityId);
    if (!modality || !modality.subModality) return [];

    return modality.subModality.map((sm) => ({
      key: sm.id,
      text: sm.serviceCode + ": " + sm.serviceName,
    }));
  };

  const getClientLabels = (clientIds) => {
    return clientIds
      .map((id) => clientOptions.find((c) => c.value === id)?.label)
      .filter(Boolean)
      .join(", ");
  };

  const selectedClientLabels =
    editingData?.clients
        ?.map(id => clientOptions.find(c => c.value === id)?.label)
        ?.filter(Boolean)
        ?.join(", ") || "";

  const handleSelectNode = (node) => {
    if (!isEditing) {
      setSelectedNodeId(node.id);
    }
  };

  const handleEditClick = (e, node) => {
    e.stopPropagation();
    setSelectedNodeId(node.id);
    setEditingData({ ...node });
    setBackupNode(JSON.parse(JSON.stringify(node)));
    setIsEditing(true);
  };

  const handleDeleteClick = (e, node) => {
    e.stopPropagation();
    setNodeToDelete(node);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async() => {
    setLoaderRunning(true); 
    ref.current.continuousStart();
    try {
        const response = await deleteAINodeDetails(nodeToDelete.id);
        console.log("AI Node Delete response:", response);

        if(response.status == 200 && response.data.result === "SUCCESS") {
            setNodes(nodes.filter((n) => n.id !== nodeToDelete.id));
            if (selectedNodeId === nodeToDelete.id) {
                setSelectedNodeId(null);
            }
            showToastv2(TOAST_TYPE.SUCCESS, "AI Node deleted successfully");
        } else {
            showToastv2(TOAST_TYPE.ERROR, "Failed to delete AI Node");
        }
        
    } catch (err) {
        console.error(err);
        showToastv2(TOAST_TYPE.ERROR, "Failed to delete AI Node");
    } finally {
        setLoaderRunning(false); ref.current.complete();
        setShowDeleteModal(false);
        setNodeToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setNodeToDelete(null);
  };

  const handleAddNew = () => {
    const newNode = {
      id: null,
      name: "",
      clients: [],
      aeTitle: "",
      hostname: "",
      port: "",
      responseAETitle: "",
      delay: "",
      rules: [],
    };
    setNodes([newNode, ...nodes]);
    setSelectedNodeId(newNode.id);
    setEditingData({ ...newNode });
    setIsEditing(true);
  };

  const validateField = (field, value) => {
    if (field === "name") {
      if (!value || !value.trim()) return "Node name is required.";
      if (value.length > MAX.name) return `Node Name can have maximum ${MAX.name} characters.`;
      return "";
    }
    if (field === "clients") {
      if (!value || value.length === 0) return "At least one client must be selected.";
      return "";
    }
    if (field === "aeTitle") {
      if (!value || !value.trim()) return "AE Title is required.";
      if (value.length > MAX.aeTitle) return `AE Title can have maximum ${MAX.aeTitle} characters.`;
      return "";
    }
    if (field === "responseAETitle") {
      if (!value || !value.trim()) return "Response AE Title is required.";
      if (value.length > MAX.responseAETitle) return `Response AE Title can have maximum ${MAX.responseAETitle} characters.`;
      return "";
    }
    if (field === "hostname") {
      if (!value || !value.trim()) return "Hostname is required.";
      if (value.length > MAX.hostname) return `Hostname can have maximum ${MAX.hostname} characters.`;
      return "";
    }
    if (field === "port") {
      if (!value || !value.toString().trim()) return "Port is required.";
      if (!/^\d+$/.test(value)) return "Port must be numeric.";
      if (value.toString().length > MAX.port) return `Port can't exceed ${MAX.port} digits.`;
      return "";
    }
    if (field === "delay") {
      if (!value || !value.toString().trim()) return "Delay is required.";
      if (!/^\d+$/.test(value)) return "Delay must be numeric.";
      if (value.toString().length > MAX.delay) return `Delay can't exceed ${MAX.delay} digits.`;
      if (Number(value) <= 0) return "Delay must be greater than 0.";
      return "";
    }
    return "";
  };

  const handleSave = async () => {
    const fieldsToValidate = [
      { field: "name", value: editingData.name },
      { field: "clients", value: editingData.clients },
      { field: "aeTitle", value: editingData.aeTitle },
      { field: "responseAETitle", value: editingData.responseAETitle },
      { field: "hostname", value: editingData.hostname },
      { field: "port", value: editingData.port },
      { field: "delay", value: editingData.delay },
    ];

    for (const item of fieldsToValidate) {
      const errorMsg = validateField(item.field, item.value);
      if (errorMsg) {
        showToastv2(TOAST_TYPE.INFO, errorMsg);
        return;
      }
    }

    if (!editingData.rules || editingData.rules.length === 0) {
      showToastv2(TOAST_TYPE.INFO, "At least one rule must be added.");
      return;
    }

    for (let i = 0; i < editingData.rules.length; i++) {
      const rule = editingData.rules[i];

      if (!rule.modalityId) {
        showToastv2(TOAST_TYPE.INFO, `Rule ${i + 1}: Modality is required.`);
        return;
      }

      //optional field , only validate lenght if exists
     if (rule.studyDescription && rule.studyDescription.length > 255) {
    showToastv2(TOAST_TYPE.INFO, `Rule ${i + 1}: Study Description cannot exceed 255 characters.`);
    return;
  }
    }

   
              const payloadToSend = {
                ...editingData,
            
              rules: editingData.rules.map(rule => {
              const transformedRule = {
                modalityId: rule.modalityId,
                studyDescription: rule.studyDescription || null, // Optional
                descriptionMatchType: rule.descriptionMatchType,
                ruleType: rule.ruleType,
              };
              
              
              if (rule.subModalityId !== null && rule.subModalityId !== undefined) {
                transformedRule.subModalityId = rule.subModalityId;
              }
              
              return transformedRule;
            })
          };

    const isNew = editingData.id === null;
   // console.log("Saving AI Node:", editingData, "Is new:", isNew);

    setLoaderRunning(true);
    ref.current.continuousStart();

    try {
        const response = await saveAINode(payloadToSend);
        console.log("Save AI Node response:", response);

        if (response.status === 200 && response.data.result === "SUCCESS") {
            const savedId = response.data.data; 
            const updatedEditingData = { ...editingData, id: savedId };
            const updatedNodes = isNew
                ? nodes.map((n) => (n.id === null ? updatedEditingData : n))
                : nodes.map((n) => (n.id === savedId ? updatedEditingData : n));
            
            showToastv2(TOAST_TYPE.SUCCESS, `AI Node ${isNew ? "created" : "updated"} successfully`);
            setNodes(updatedNodes);
            setIsEditing(false);
            setEditingData(null);
            setSelectedNodeId(savedId);
        }
    } catch (err) {
        console.error(err);
        showToastv2(TOAST_TYPE.ERROR, "Failed to save AI Node");
    } finally {
        setLoaderRunning(false);
        ref.current.complete();
    }
  };

  const handleCancel = () => {
    if (editingData?.id === null) {
      setNodes(nodes.filter(n => n.id !== null));
      setSelectedNodeId(null);
    } else {
      setNodes(nodes.map(n =>
        n.id === backupNode.id ? backupNode : n
      ));
    }

    setIsEditing(false);
    setEditingData(null);
    setBackupNode(null);
  };

  const updateEditingField = (field, value) => {
    setEditingData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClientChange = (clientValues) => {
    updateEditingField("clients", clientValues);
 };

  const handleAddRule = () => {
    updateEditingField("rules", [
      ...editingData.rules,
      { modalityId: null, subModalityId: null , studyDescription: "", descriptionMatchType: 0, ruleType: 0},
    ]);
  };

  const handleUpdateRule = (index, field, value) => {
    const updatedRules = [...editingData.rules];
    updatedRules[index][field] = value;
    updateEditingField("rules", updatedRules);
  };

  const handleDeleteRule = (index) => {
    const updatedRules = editingData.rules.filter((_, i) => i !== index);
    updateEditingField("rules", updatedRules);
  };

  const handleEchoTest = async () => {
    console.log("echo pacs: ", editingData, selectedNode);
    const node = isEditing ? editingData : selectedNode;
    if (
      node.name == "" ||
      node.aeTitle == "" ||
      node.port == "" ||
      node.hostname == "" ||
      node.responseAETitle == "" ||
      node.delay == ""
    ) {
      showToastv2(TOAST_TYPE.SUSPECT, "Please fill all required fields.");
      return;
    }

    setLoaderRunning(true);
    ref.current.continuousStart();

    const nodeConfig = {
      aeTitle: node.aeTitle,
      hostname: node.hostname,
      port: node.port,
      name: node.name,
    };

    const response = await echoPacsNode(nodeConfig);
    console.log(response);

    if (response.status == 200) {
      if (response.data.data.status == "SUCCESS") {
        showToastv2(
          TOAST_TYPE.SUCCESS,
          "Echo successful! Connection to PACS node verified."
        );
      } else {
        showToastv2(
          TOAST_TYPE.SUSPECT,
          "Echo failed! Unable to connect to PACS node."
        );
      }
    } else {
      showToastv2(TOAST_TYPE.ERROR, "Some error occured.");
    }
    setLoaderRunning(false);
    ref.current.complete();
  };

  const getModalityName = (id) => {
    const m = modalitiesSubModalities.find(x => x.id === id);
    return m ? m.name : "-";
  };

  const getSubmodalityLabel = (subId) => {
    for (const m of modalitiesSubModalities) {
        const sm = m.subModality?.find(s => s.id === subId);
        if (sm) return `${sm.serviceCode}: ${sm.serviceName}`;
    }
    return "-";
  };

  const getDescriptionMatchType = (type) => {
    const m = descriptionTypeOptions.find(x => x.key === type);
    return m ? m.text : "-";
  };

  const getRuleType = (type) => {
    const m = ruleTypeOptions.find(x => x.key === type);
    return m ? m.text : "-";
  };
  
  const displayNode = isEditing ? editingData : selectedNode;
  const empty = nodes.length === 0;

  const dropDownStyle = {
    dropdown: {
      width: "100%"
    },
    dropdownItemSelected: {
      fontSize: "12px",
      lineHeight: "18px",
      fontWeight: "600",
    },
    dropdownItemsWrapper: {
      maxHeight: "300px",
      overflowY: 'auto',
    },
    dropdownItem: {
      fontSize: "12px",
      lineHeight: "18px",
      fontWeight: "600",
      color: "white"
    },
  };

   const handleClickOutside = () => {
    setFilteredModalityOptions(modalityOptions);
    setFilteredSubModalityOptions({});
  };

  const getFilteredSubmodalityOptions = (ruleIndex, modalityId) => {
    if (filteredSubModalityOptions[ruleIndex]) {
      return filteredSubModalityOptions[ruleIndex];
    }
    return getSubmodalityOptions(modalityId);
  };

  const onDropDownSearch = (searchValue, initialValues) => {
    if (searchValue === null || searchValue === "") {
      return [...initialValues];
    }
    const filteredOptions = [...initialValues].filter(
      (i) => {
        const text = i.text.toLowerCase();
        return text.includes(searchValue.toLowerCase());
      }
    );
    return filteredOptions;
  };

  return (
    <div
      className="ai-nodes-container"
    >
      <div className="breadCrumb">
        <CustomBreadCrumb items={[{ text: t("AI Nodes Configuration") }]} />
      </div>

      {empty && (
        <div className="empty-node-wrapper">
          <p>{t("No nodes configured yet")}</p>
          <PrimaryButton
            text={t("Add New Node")}
            disabled={isEditing}
            onClick={handleAddNew}
          />
        </div>
      )}
      {!empty && (
        <>
          <div className="toolbar">
            <PrimaryButton onClick={handleAddNew} disabled={isEditing}>
              Add New Node
            </PrimaryButton>
          </div>
          <div className="split-container">
            <div className="left-panel">
              <table className="nodes-table">
                <thead>
                  <tr>
                    <th style={{ width: "30%", textAlign: "left" }}>
                      Node Name
                    </th>
                    <th style={{ width: "50%", textAlign: "left" }}>Clients</th>
                    <th style={{ width: "20%", textAlign: "left" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {nodes.map((node) => {
                    const isSelected = node.id === selectedNodeId;
                    const isThisEditing = isEditing && isSelected;

                    return (
                      <tr
                        key={node.id}
                        className={isEditing ? "disable-hover" : ""}
                        onClick={() => handleSelectNode(node)}
                        style={{
                          background: isSelected ? accentBlue : "transparent",
                        }}
                      >
                        <td>
                          {isThisEditing ? (
                            <TextField
                              name="name"
                              value={editingData.name}
                              onChange={(e) =>
                                updateEditingField("name", e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Enter node name"
                            />
                          ) : (
                            <CustomToolTipHost content={node.name ||" No Clients"}>
                                <div className="single-line-ellipsis">
                                   {node.name || (
                                    <span className="placeholder">New Node</span>
                                    )}
                                </div>
                            </CustomToolTipHost>
                            
                          )}
                        </td>
                        <td>
                          {isThisEditing ? (
                            <CustomToolTipHost content={selectedClientLabels}>
                                <MultiSelectClientDropdown
                                options={clientOptions}
                                value={clientOptions.filter((o) =>
                                    (editingData?.clients ?? []).includes(o.value)
                                )}
                                onChange={(selectedOptions) =>
                                    handleClientChange(selectedOptions ? selectedOptions.map(opt => opt.value) : [])
                                }
                                placeholder="Select clients..."
                                getThemeColor={getThemeColor}
                                addSelectAll={false}
                                /> 
                            </CustomToolTipHost>
                          ) : (
                            <CustomToolTipHost content={getClientLabels(node.clients)}>
                                <div className="single-line-ellipsis">
                                    {getClientLabels(node.clients) || (
                                    <span className="placeholder">No clients</span>
                                    )}
                                </div>
                            </CustomToolTipHost>
                          )}
                        </td>
                        <td>
                          {!isThisEditing && (
                            <div className="actions">
                                <CustomToolTipHost content= "Edit">
                                    <Icon 
                                        className={`edit-icon ${isEditing ? "disabled-icon" : ""}`}
                                        iconName="Edit"
                                        styles={{
                                        root: {
                                            cursor: isEditing
                                            ? "not-allowed"
                                            : "pointer",
                                            marginRight: 8,
                                        },
                                        }}
                                        onClick={(e) => isEditing ? null : handleEditClick(e, node)}
                                    />
                                </CustomToolTipHost>
                                <CustomToolTipHost content= "Delete">
                                    <Icon
                                        className={`delete-icon ${isEditing ? "disabled-icon" : ""}`}
                                        iconName="Delete"
                                        styles={{
                                        root: {
                                            cursor: isEditing
                                            ? "not-allowed"
                                            : "pointer",
                                        },
                                        }}
                                        onClick={(e) => isEditing ? null : handleDeleteClick(e, node)}
                                    />
                                </CustomToolTipHost>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="right-panel" style={{ background: accentBlue }}>
              {displayNode ? (
                <>
                <div className="top-details-container">
                  <div className="details-header">
                    <h2>Node Configuration</h2>
                  </div>

                  <div className="details-grid">
                    <div className="detail-item">
                      <label>AE Title <span style={requiredStyle}>*</span></label>
                      {isEditing ? (
                        <TextField
                          type="text"
                          value={editingData.aeTitle}
                          onChange={(e) =>
                            updateEditingField("aeTitle", e.target.value)
                          }
                          placeholder="Enter AE Title"
                        />
                      ) : (
                        <div className="detail-value">
                          {displayNode.aeTitle || "-"}
                        </div>
                      )}
                    </div>

                    <div className="detail-item">
                      <label>Hostname <span style={requiredStyle}>*</span></label>
                      {isEditing ? (
                        <TextField
                          type="text"
                          value={editingData.hostname}
                          onChange={(e) =>
                            updateEditingField("hostname", e.target.value)
                          }
                          placeholder="Enter Host Name"
                        />
                      ) : (
                        <div className="detail-value">
                          {displayNode.hostname || "-"}
                        </div>
                      )}
                    </div>

                    <div className="detail-item">
                      <label>Port <span style={requiredStyle}>*</span></label>
                      {isEditing ? (
                        <TextField
                          type="number"
                          value={editingData.port}
                          onChange={(e) =>
                            updateEditingField("port", e.target.value)
                          }
                          placeholder="Enter port"
                        />
                      ) : (
                        <div className="detail-value">
                          {displayNode.port || "-"}
                        </div>
                      )}
                    </div>

                    <div className="detail-item">
                      <label>Response AE Title <span style={requiredStyle}>*</span></label>
                      {isEditing ? (
                        <TextField
                          type="text"
                          value={editingData.responseAETitle}
                          onChange={(e) =>
                            updateEditingField("responseAETitle", e.target.value)
                          }
                          placeholder="Enter response AE title"
                        />
                      ) : (
                        <div className="detail-value">
                          {displayNode.responseAETitle || "-"}
                        </div>
                      )}
                    </div>

                    <div className="detail-item">
                      <label>Delay (seconds) <span style={requiredStyle}>*</span></label>
                      {isEditing ? (
                        <TextField
                          type="number"
                          min={1}
                          value={editingData.delay}
                          onChange={(e) =>
                            updateEditingField("delay", e.target.value)
                          }
                          placeholder="Enter delay"
                        />
                      ) : (
                        <div className="detail-value">
                          {displayNode.delay || "-"}
                        </div>
                      )}
                    </div>

                    <div className="detail-item echo-item">
                      <label>Action</label>
                      <PrimaryButton onClick={handleEchoTest} style={{width: '50px'}}>
                        Echo
                      </PrimaryButton>
                    </div>
                  </div>

                  <div className="rules-section">
                    <div className="rules-header">
                      <h3>Rules Configuration</h3>
                    </div>

                    {displayNode.rules.length === 0 ? (
                      <div className="no-rules">No rules configured</div>
                    ) : (
                      <div className="modal-scroll rules-table-wrapper">
                        <table className="rules-table2">
                          <thead>
                            <tr>
                              <th style={{ textAlign: "left" }}>Rule Type
                                <span style={requiredStyle}>*</span>
                              </th>
                              <th style={{ textAlign: "left" }}>Modality
                                <span style={requiredStyle}>*</span>
                              </th>
                              <th style={{ textAlign: "left" }}>Sub Modality</th>
                              <th style={{ textAlign: "left" }}>Study Description</th>
                              <th style={{ textAlign: "left" }}>Description Match</th>
                              <th style={{ textAlign: "left" }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayNode.rules.map((rule, idx) => (
                              <tr key={idx}>
                                {isEditing ? (
                                  <>
                                    <td style={{ width: "20%" }}>
                                      <div className="match-type-group">
                                        <ChoiceGroup
                                          selectedKey={
                                            rule.ruleType || 0
                                          }
                                          options={ruleTypeOptions}
                                          styles={choiceGroupStyles}
                                          //disabled={!rule.studyDescription}
                                          onChange={(e, option) =>
                                            handleUpdateRule(
                                              idx,
                                              "ruleType",
                                              option.key
                                            )
                                          }
                                        />
                                      </div>
                                    </td>
                                    <td style={{ width: "20%" }}>
                                      <OutsideClickHandler onOutsideClick={handleClickOutside}>
                                        <SearchableDropDown
                                          dropDownStyle={dropDownStyle}
                                          selectedKey={rule.modalityId}
                                          onChange={(e, option) =>
                                          handleUpdateRule(idx, "modalityId", option?.key)}
                                          onSearchValueChanged={(searchValue) => {
                                            console.log("search value ", searchValue);
                                            const newOptions = onDropDownSearch(
                                              searchValue,
                                              modalityOptions
                                            );
                                            setFilteredModalityOptions(newOptions);
                                          }}
                                          options={filteredModalityOptions}
                                          className="ai-node-modality-dropdown modal-scroll"
                                          placeholder="Select Modality"
                                          
                                        />
                                      </OutsideClickHandler>
                                    </td>
                                    <td style={{ width: "30%" }}>
                                      <div className="submodality-column">
                                      <OutsideClickHandler onOutsideClick={handleClickOutside}>
                                        <SearchableDropDown
                                          placeholder="Select Sub Modality"
                                          options={getFilteredSubmodalityOptions(idx, rule.modalityId)}
                                          selectedKey={rule.subModalityId}
                                          onChange={(e, option) =>
                                              handleUpdateRule(idx, "subModalityId", option?.key)
                                          }
                                          className="ai-node-sub-modality-dropdown modal-scroll"
                                          dropDownStyle={dropDownStyle}
                                          onSearchValueChanged={(searchValue) => {
                                            console.log("search value ", searchValue);
                                            const allSubModalityOptions = getSubmodalityOptions(rule.modalityId);
                                            const newOptions = onDropDownSearch(
                                              searchValue,
                                              allSubModalityOptions
                                            );
                                            setFilteredSubModalityOptions(prev => ({
                                              ...prev,
                                              [idx]: newOptions
                                            }));
                                          }}
                                          disabled={!!rule.studyDescription}
                                        />
                                      </OutsideClickHandler>
                                      {rule.subModalityId && (
                                        <div
                                          className="cursor-pointer"
                                          onClick={() => handleUpdateRule(idx, "subModalityId", null)}
                                          style={{ paddingLeft: "5px" }}
                                        >
                                          <CustomToolTipHost content="Clear">
                                            <ClearIcon></ClearIcon>
                                          </CustomToolTipHost>
                                        </div>
                                        )}
                                      </div>
                                    </td>
                                    <td style={{ width: "25%" }}>
                                      <TextField
                                        value={rule.studyDescription}
                                        onChange={(e) =>
                                          handleUpdateRule(
                                            idx,
                                            "studyDescription",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Study Description"
                                        disabled={!!rule.subModalityId}
                                      />
                                    </td>
                                    <td style={{ width: "20%" }}>
                                      <div className="match-type-group">
                                        <ChoiceGroup
                                          selectedKey={
                                            rule.descriptionMatchType
                                          }
                                          options={descriptionTypeOptions}
                                          styles={choiceGroupStyles}
                                          disabled={!rule.studyDescription}
                                          onChange={(e, option) =>
                                            handleUpdateRule(
                                              idx,
                                              "descriptionMatchType",
                                              option.key
                                            )
                                          }
                                        />
                                      </div>
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                        <CustomToolTipHost content = "Delete">
                                            <Icon
                                                className="delete-icon"
                                                iconName="Delete"
                                                styles={{ root: { cursor: "pointer" } }}
                                                onClick={(e) => handleDeleteRule(idx)}
                                            />
                                      </CustomToolTipHost>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td>{getRuleType(rule.ruleType || 0)}</td>
                                    <td>{getModalityName(rule.modalityId) || "-"}</td>
                                    <td>{getSubmodalityLabel(rule.subModalityId) || "-"}</td>
                                    <td>{rule.studyDescription || "-"}</td>
                                    <td>
                                      {rule.studyDescription
                                        ? getDescriptionMatchType(rule.descriptionMatchType)
                                        : "-"}
                                    </td>
                                    <td>-</td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                {isEditing && (
                    <div className="action-buttons-strip">
                      <PrimaryButton onClick={handleAddRule}>
                        + Add Rule
                      </PrimaryButton>
                      <div className="button-group-left">
                        <DefaultButton onClick={handleCancel}>
                          Cancel
                        </DefaultButton>
                        <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-selection">
                  <p>Select a node from the left panel to view details</p>
                </div>
              )}
            </div>
          </div>{" "}
        </>
      )}

      {showDeleteModal && (
        <ConfirmModal
            handleConfirm={handleConfirmDelete}
            handleNotConfirm={handleCancelDelete}
            showConfirmModal={showDeleteModal}
            message={t("string-literals.delete-ai-node")}
            title={t("confirm-message.confirm-delete-node")}
            confirmButtonText={t("confirm-message.yes")}
        />
      )}
    </div>
  );
};

export default AINodesConfig;